import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IcnChevron } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useMobileFloatingSheet } from '../shared/mobileFloating.js';
import { announce } from './announce.js';
import { handleSelectKey } from './keyboard.js';
import { SelectPanel } from './SelectPanel.js';
import type { SelectGroup, SelectOption, SelectProps } from './types.js';
import {
  computeVisibleIndices,
  ensurePortalRoot,
  findEnabledIndex,
  optionLabelText,
} from './utils.js';

export type { SelectOption, SelectGroup, SelectProps, SelectFilter } from './types.js';

const TYPEAHEAD_TIMEOUT = 1000;
const PANEL_MAX_HEIGHT = 360;

/** Fully self-developed Select — button trigger + portal listbox.
 *  No native <select>, no system picker, no third-party dropdown lib.
 *  Features:
 *    - Keyboard: Space/Enter open, ↓↑ navigate, Home/End extremes,
 *      Esc close, Enter/Tab commit, typeahead (1s buffer).
 *    - click-outside close; auto flip top/bottom; max-height resize.
 *    - Optional `searchable` with contains/fuzzy filter.
 *    - `groups` renders sticky group headers; empty groups collapse.
 *    - Live-region announces "已选 X" / "Selected X" after commit.
 *    - forced-colors (Windows High Contrast) honored via CSS. */
export function Select<V extends string = string>(props: SelectProps<V>): ReactNode {
  const t = useI18nT();
  const {
    options,
    groups,
    value: controlledValue,
    defaultValue = null,
    onValueChange,
    placeholder,
    disabled = false,
    invalid = false,
    name,
    size = 'md',
    className,
    ref,
    id,
    searchable = false,
    searchPlaceholder = t('select.searchPlaceholder'),
    filter = 'contains',
    filterFn,
    onHighlightChange,
    renderEmpty,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;

  const isControlled = controlledValue !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState<V | null>(defaultValue);
  const value = isControlled ? (controlledValue ?? null) : uncontrolledValue;

  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const [query, setQuery] = useState('');

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const typeaheadRef = useRef<{ buffer: string; timer: number | null }>({
    buffer: '',
    timer: null,
  });

  const reactId = useId();
  const panelId = `${id ?? reactId}-panel`;
  const triggerId = id ?? `${reactId}-trigger`;

  const {
    setAnchor,
    setFloating,
    rect: floatingRect,
    floatingStyle,
    maxHeight: floatingMaxHeight,
    anchorVisible,
    update: updateFloating,
  } = useFloating({
    open,
    placement: 'bottom-start',
    gap: 6,
    padding: 8,
    matchAnchorWidth: true,
    hideWhenDetached: true,
  });
  const mobileSheet = useMobileFloatingSheet(open);

  const selectedIndex = useMemo(
    () => (value === null ? -1 : options.findIndex((o) => o.value === value)),
    [options, value],
  );
  const selectedOption: SelectOption<V> | null =
    selectedIndex >= 0 ? (options[selectedIndex] ?? null) : null;

  const visibleSet = useMemo(
    () => new Set(computeVisibleIndices(options, query, filter, filterFn)),
    [options, query, filter, filterFn],
  );
  const totalVisible = visibleSet.size;
  const isVisible = useCallback((i: number) => visibleSet.has(i), [visibleSet]);

  // Group organization: preserve group order from `groups` prop; options
  // without a groupId fall into a synthetic "__ungrouped" bucket rendered
  // last. Groups with zero visible options are collapsed.
  const groupOrder = useMemo<{ group: SelectGroup | null; optionIndices: number[] }[]>(() => {
    const byGroup = new Map<string, number[]>();
    const ungrouped: number[] = [];
    for (let i = 0; i < options.length; i += 1) {
      if (!isVisible(i)) continue;
      const opt = options[i];
      if (opt === undefined) continue;
      if (opt.groupId !== undefined) {
        const bucket = byGroup.get(opt.groupId) ?? [];
        bucket.push(i);
        byGroup.set(opt.groupId, bucket);
      } else {
        ungrouped.push(i);
      }
    }
    const rows: { group: SelectGroup | null; optionIndices: number[] }[] = [];
    if (groups !== undefined) {
      for (const g of groups) {
        const idxs = byGroup.get(g.id);
        if (idxs !== undefined && idxs.length > 0) rows.push({ group: g, optionIndices: idxs });
      }
    }
    if (ungrouped.length > 0) rows.push({ group: null, optionIndices: ungrouped });
    return rows;
  }, [groups, options, isVisible]);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      setAnchor(node);
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLButtonElement | null }).current = node;
      }
    },
    [ref, setAnchor],
  );

  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      setFloating(node);
    },
    [setFloating],
  );

  const commit = useCallback(
    (opt: SelectOption<V>) => {
      if (opt.disabled === true) return;
      if (!isControlled) setUncontrolledValue(opt.value);
      onValueChange?.(opt.value, opt);
      announce(t('select.announceSelected', { label: optionLabelText(opt.label) }));
    },
    [isControlled, onValueChange, t],
  );

  const closePanel = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setQuery('');
    // Clear any in-flight typeahead so a buffer like "ap" from a previous
    // open doesn't get prepended to the next session's first keystroke.
    const ts = typeaheadRef.current;
    if (ts.timer !== null) {
      window.clearTimeout(ts.timer);
      ts.timer = null;
    }
    ts.buffer = '';
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const updateHighlight = useCallback(
    (next: number) => {
      setHighlightIndex(next);
      const opt = next >= 0 ? (options[next] ?? null) : null;
      onHighlightChange?.(opt);
    },
    [options, onHighlightChange],
  );

  const openPanel = useCallback(
    (initial: 'selected' | 'first' | 'last' | number) => {
      if (disabled) return;
      let idx: number;
      if (initial === 'selected') {
        idx =
          selectedIndex >= 0 &&
          options[selectedIndex]?.disabled !== true &&
          isVisible(selectedIndex)
            ? selectedIndex
            : findEnabledIndex(options, -1, 1, isVisible);
      } else if (initial === 'first') {
        idx = findEnabledIndex(options, -1, 1, isVisible);
      } else if (initial === 'last') {
        idx = findEnabledIndex(options, 0, -1, isVisible);
      } else {
        idx = initial;
      }
      updateHighlight(idx);
      setOpen(true);
    },
    [disabled, options, selectedIndex, isVisible, updateHighlight],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (target === null) return;
      if (triggerRef.current?.contains(target) === true) return;
      if (panelRef.current?.contains(target) === true) return;
      closePanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open, closePanel]);

  useEffect(() => {
    if (mobileSheet) return undefined;
    if (!open) return undefined;
    const handler = (event: Event): void => {
      const target = event.target;
      if (target instanceof Node && panelRef.current?.contains(target) === true) return;
      closePanel(false);
    };
    window.addEventListener('scroll', handler, true);
    document.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('scroll', handler, true);
      document.removeEventListener('scroll', handler, true);
    };
  }, [open, mobileSheet, closePanel]);

  useEffect(() => {
    if (open && !mobileSheet && !anchorVisible) closePanel(false);
  }, [open, mobileSheet, anchorVisible, closePanel]);

  // Auto-focus search when panel opens; refocus trigger when highlight moves
  // on keyboard but search has focus, etc. Strategy: while open, search owns
  // typing; keyboard nav is intercepted at the document level.
  useEffect(() => {
    if (!open || !searchable) return;
    searchInputRef.current?.focus();
  }, [open, searchable]);

  useLayoutEffect(() => {
    if (!open) return;
    updateFloating();
  }, [open, query, searchable, totalVisible, updateFloating]);

  // Keep highlight valid as visible set changes (typing narrows options).
  useEffect(() => {
    if (!open) return;
    if (highlightIndex < 0 || !isVisible(highlightIndex)) {
      updateHighlight(findEnabledIndex(options, -1, 1, isVisible));
    }
  }, [open, highlightIndex, isVisible, options, updateHighlight]);

  useLayoutEffect(() => {
    if (!open || highlightIndex < 0) return;
    const panel = panelRef.current;
    if (panel === null) return;
    const el = panel.querySelector<HTMLElement>(`[data-option-index="${String(highlightIndex)}"]`);
    if (el === null) return;
    const panelRect = panel.getBoundingClientRect();
    const optRect = el.getBoundingClientRect();
    if (optRect.top < panelRect.top) {
      panel.scrollTop += optRect.top - panelRect.top;
    } else if (optRect.bottom > panelRect.bottom) {
      panel.scrollTop += optRect.bottom - panelRect.bottom;
    }
  }, [open, highlightIndex]);

  const typeaheadMatch = useCallback(
    (char: string): void => {
      const state = typeaheadRef.current;
      if (state.timer !== null) window.clearTimeout(state.timer);
      state.buffer += char.toLocaleLowerCase();
      state.timer = window.setTimeout(() => {
        state.buffer = '';
        state.timer = null;
      }, TYPEAHEAD_TIMEOUT);
      const startFrom = open ? highlightIndex : selectedIndex;
      const len = options.length;
      for (let step = 1; step <= len; step += 1) {
        const i = (((startFrom + step) % len) + len) % len;
        const opt = options[i];
        if (opt === undefined || opt.disabled === true) continue;
        const text = optionLabelText(opt.label).toLocaleLowerCase();
        if (text.startsWith(state.buffer)) {
          if (open) updateHighlight(i);
          else commit(opt);
          return;
        }
      }
    },
    [open, highlightIndex, selectedIndex, options, commit, updateHighlight],
  );

  const onKey = (e: ReactKeyboardEvent<HTMLElement>): void => {
    handleSelectKey(
      e,
      { disabled, open, searchable, options, highlightIndex, isVisible },
      { openPanel, closePanel, updateHighlight, commit, typeaheadMatch },
    );
  };

  const onTriggerClick = (): void => {
    if (disabled) return;
    if (open) closePanel(false);
    else openPanel('selected');
  };

  const rootClasses = ['vq-select', className].filter(Boolean).join(' ');
  const stateAttr = disabled ? 'disabled' : invalid ? 'invalid' : open ? 'open' : 'idle';

  const portalRoot = ensurePortalRoot();
  const activeDescendantId =
    open && highlightIndex >= 0 ? `${panelId}-opt-${String(highlightIndex)}` : undefined;
  const panelMaxHeight =
    floatingMaxHeight > 0 ? Math.min(PANEL_MAX_HEIGHT, floatingMaxHeight) : PANEL_MAX_HEIGHT;

  return (
    <span className={rootClasses} data-component="Select" data-size={size} data-state={stateAttr}>
      <button
        ref={setTriggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-activedescendant={activeDescendantId}
        aria-disabled={disabled || undefined}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        className="vq-select__trigger"
        data-slot="trigger"
        onClick={onTriggerClick}
        onKeyDown={onKey}
      >
        <span
          className="vq-select__value"
          data-slot={selectedOption === null ? 'placeholder' : 'value'}
        >
          {selectedOption !== null ? selectedOption.label : (placeholder ?? '')}
        </span>
        <span className="vq-select__chevron" aria-hidden="true" data-slot="indicator">
          <IcnChevron size={12} />
        </span>
      </button>

      {name !== undefined ? <input type="hidden" name={name} value={value ?? ''} /> : null}

      {open && portalRoot !== null
        ? createPortal(
            <SelectPanel
              panelId={panelId}
              triggerId={triggerId}
              floatingStyle={floatingStyle}
              maxHeight={panelMaxHeight}
              placement={mobileSheet ? 'bottom' : floatingRect?.side === 'top' ? 'top' : 'bottom'}
              mobileSheet={mobileSheet}
              options={options}
              groupOrder={groupOrder}
              visibleCount={totalVisible}
              highlightIndex={highlightIndex}
              selectedIndex={selectedIndex}
              searchable={searchable}
              searchPlaceholder={searchPlaceholder}
              query={query}
              ariaLabelledBy={ariaLabelledBy}
              onQueryChange={setQuery}
              onKeyDown={onKey}
              onOptionHover={updateHighlight}
              onOptionClick={(opt) => {
                commit(opt);
                closePanel(true);
              }}
              renderEmpty={renderEmpty}
              panelRef={setPanelRef}
              searchInputRef={searchInputRef}
            />,
            portalRoot,
          )
        : null}
    </span>
  );
}
